/**
 * BottomTabs Helper Components
 * Extracted components to reduce main component complexity
 */

import React from 'react';
import type { TabItem } from './BottomTabs';

/**
 * Tab Badge Component - Shows notification count
 */
export const TabBadge: React.FC<{ badge?: number | string }> = ({ badge }) => {
  if (!badge) return null;
  
  const badgeText = typeof badge === 'number' && badge > 99 ? '99+' : String(badge);
  
  return (
    <div 
      className="tab-badge"
      role="status" 
      aria-label={`${badgeText} notifications`}
    >
      <span>{badgeText}</span>
    </div>
  );
};

/**
 * Tab Button Component - Individual tab button
 */
export const TabButton: React.FC<{
  tab: TabItem;
  isActive: boolean;
  activeColor: string;
  inactiveColor: string;
  testID: string;
  onPress: (tab: TabItem) => void;
}> = ({ tab, isActive, activeColor, inactiveColor, testID, onPress }) => {
  const getIcon = () => {
    const iconToUse = isActive && tab.activeIcon ? tab.activeIcon : tab.icon;
    return iconToUse;
  };
  
  const getColor = () => {
    return isActive ? activeColor : inactiveColor;
  };
  
  const getTabStyle = () => ({ 
    color: getColor(),
    opacity: tab.disabled ? 0.3 : 1,
    cursor: tab.disabled ? 'not-allowed' : 'pointer',
    minHeight: '44px'
  });
  
  const getIconFilter = () => {
    return isActive 
      ? 'brightness(0) saturate(100%) invert(45%) sepia(93%) saturate(1352%) hue-rotate(166deg) brightness(100%) contrast(101%)'
      : 'brightness(0) saturate(100%) invert(48%) sepia(8%) saturate(863%) hue-rotate(176deg) brightness(93%) contrast(86%)';
  };
  
  const icon = getIcon();
  const isReactElement = React.isValidElement(icon);
  
  return (
    <button
      onClick={() => onPress(tab)}
      className={`tab-button ${isActive ? 'active' : ''} ${tab.disabled ? 'disabled' : ''} ${tab.isAction ? 'action-button' : ''}`}
      style={getTabStyle()}
      disabled={tab.disabled}
      aria-label={tab.ariaLabel || tab.label}
      aria-current={isActive ? 'page' : undefined}
      data-testid={`${testID}-tab-${tab.id}`}
    >
      <span className="tab-icon-container">
        {isReactElement ? (
          <span className="tab-icon">
            {icon}
          </span>
        ) : (
          <img 
            src={icon as string} 
            alt=""
            className="tab-icon"
            style={{ filter: getIconFilter() }}
          />
        )}
        <TabBadge badge={tab.badge} />
      </span>
      <span className="tab-label">
        {tab.label}
      </span>
    </button>
  );
};

/**
 * Platform-specific helpers
 */
export function getPlatformSafeAreaInsets(): { bottom: number } {
  try {
    const safeAreaBottom = parseInt(
      getComputedStyle(document.documentElement)
        .getPropertyValue('--sat') || '0'
    ) || 0;
    return { bottom: Math.max(safeAreaBottom, 34) }; // iPhone X+ default
  } catch {
    return { bottom: 0 };
  }
}